import { i18n } from "@pathwright/ui/src/components/lng/I18nextProvider"
import { DockPanelContext } from "@pathwright/ui/src/components/panel/DockPanel"
import ScrollView from "@pathwright/ui/src/components/scroll/ScrollView"
import { COHORT_STATUS_COMPLETED } from "@pathwright/web/src/modules/group/constants"
// import { withCohortSyncPlanContext } from "@pathwright/web/src/modules/path/sync/SyncPlanContext"
import { wrapMarionetteView } from "lib/core/react/react-utils"
import get from "lodash/get"

const NoteCollection = require("note/models/note-collection").default
const Note = require("note/models/note").default
const NotePanelView = require("./views/note-panel").default
const WrappedNotePanelView = wrapMarionetteView(NotePanelView)
// Since we can't import into legacy/web from legacy/client... this is a stop gap till we update notes
window.WrappedNotePanelView = WrappedNotePanelView

class NoteStore extends require("lib/static-shim").default(
  require("lib/core/store/backbone-store").default
) {
  static initClass() {
    ;`Handles notes on a path`

    this.prototype.storeTriggers = {
      "note:removed"(note) {
        return note
      }
    }

    this.prototype.storeTriggerHandlers = {
      path: {
        "change:path"() {
          const { path } = this.getStore("path").getState()
          if (!path) {
            return this.action.clearNotes()
          }
        }
      }
    }

    this.prototype.resources = {
      note: {
        model: Note,
        collection: NoteCollection
      }
    }

    this.prototype.actions = {
      loadAndGroupNotes(forceReload = false) {
        return this.action.loadNotes(forceReload).promise.then(() => {
          return this.action.groupNotesByTarget()
        })
      },

      showNotesForStep() {
        // Shortcut to show notes for the active step (step store)
        const stepStore = this.getStore("step")
        const step = stepStore.get("step")
        const title = i18n.t("notes.notes_for", {
          title: step.get("name")
        })
        const target_id = step.get("sync_source_id")
        return this.action
          .setNotes(stepStore.get("notes"))
          .groupNotesByTarget()
          .showNotes({
            view: "list",
            title,
            target_id,
            notes: stepStore.get("notes")
          })
      },

      showNotes(options) {
        this.action.setItem(options.target_id)
        this.request.showNotePanel(options)
        // Just assuming this is the notes panel closing
        return window.App.vent.once("panel:closed", this.action.onCloseNotes)
      },

      closeNotes() {
        return App.getStore("layout").action.closeIn("panel")
      },

      onCloseNotes() {
        this._mutate({ item: null })
        const navStore = window.App.getStore("navigation")
        const { url } = navStore.getState()
        return navStore.action.navigate(url.replace(/\/notes\/(\d+\/)?$/, "/"))
      },

      setNotes(notes) {
        this.get("notes")
          ? this.get("notes").add(notes.models)
          : this._mutate({ notes })
        return this.action.setNoteCounter()
      },

      setNoteCounter() {
        const notes = this.get("notes")
        // Potentially notes will not be set yet in the store.
        // Have not been able to replicate, but this case appears to
        // occurr after loadNotes is called.
        if (notes) {
          this._mutate({ note_counter: notes.length })
        }
      },

      loadNotes(forceReload) {
        if (forceReload == null) {
          forceReload = false
        }
        const ctx = this.request.getContextStr()
        return this.resource.note
          .list({ context: ctx }, forceReload)
          .then(() => {
            return this.action.setNoteCounter()
          })
      },

      saveNote(data) {
        const { body, title, visibility, id } = data
        const context_str = this.request.getContextStr()
        // reset note
        const note = new Note({ context_str, body, title, visibility, id })
        return note.save().then(() => {
          this.action.addNote(note)
        })
      },

      clearNotes() {
        this.resource.note.clearCache()
        return this.reset()
      },

      addNote(note) {
        const notes = this.get("notes")
        notes.add(note)
        this.action.setNotes(notes).groupNotesByTarget()
      },

      removeNote(note) {
        const notes = this.get("notes")
        notes.remove(note)
        this.action.setNotes(notes).groupNotesByTarget()
        this.storeTrigger("note:removed", note)
      },

      groupNotesByTarget() {
        const notes_per_target = {}
        const all_path_items = this._getAllPathItems()

        for (let item of Array.from(all_path_items)) {
          var group_target
          const target = (notes_per_target[item.sync_source_id] = {
            notes: new NoteCollection(),
            total_notes: 0,
            item
          })

          const notes = filtered
            ? filtered.slice()
            : this.get("notes")?.models || []

          var filtered = []
          // Only retaining notes that have not been added to
          // an item's notes collection. Cut down on looping.
          for (let note of Array.from(notes)) {
            if (
              note.get("context").step === item.id ||
              note.get("context").step === item.sync_source_id
            ) {
              target.notes.add(note)
            } else {
              filtered.push(note)
            }
          }

          // Track child note count
          if (item.item_type === "G") {
            target.total_notes += target.notes.length
            group_target = target
          } else if (item.item_type === "A" || item.item_type === "R") {
            target.total_notes = target.notes.length
            if (group_target) group_target.total_notes += target.total_notes
          }
        }
        this._mutate({ notes_per_target })
      },

      setItem(sync_source_id) {
        this._mutate({ item: this.request.getItemBySourceID(sync_source_id) })
      }
    }

    this.prototype.requests = {
      getNotesForSourceItemID(sync_source_id) {
        return get(this.get("notes_per_target")[sync_source_id], "notes", null)
      },

      getNotesForSourceItem(item) {
        return get(
          this.get("notes_per_target")[item.sync_source_id],
          "notes",
          null
        )
      },

      getTargetForItem(item) {
        let sync_source_id
        if (_.isFunction(item.get)) {
          sync_source_id = item.get("sync_source_id")
        } else {
          ;({ sync_source_id } = item)
        }
        return this.get("notes_per_target")[sync_source_id]
      },

      // Probably missing something in the path store,
      // but trying to reliably select a path item model (not just an object)
      getItemBySourceID(sync_source_id) {
        const { notes_per_target } = this.getState()
        const notes = notes_per_target[sync_source_id]
        if (notes) {
          return notes.item
        }
        return this.getStore("path").request.findItem(sync_source_id)
      },

      getNoteFilterMenuData(selected) {
        const data = {
          label: "",
          options: []
        }
        // All path items excluding dividers

        const all_path_items = this._getAllPathItems()
        for (let item of Array.from(all_path_items)) {
          var name
          let label = (name = item.name)
          if (item.item_type === "A") {
            const { step_verb } = item
            const verb = step_verb[0].toUpperCase() + step_verb.slice(1)
            const icon = `pwai-${step_verb.toLowerCase()}`
            label = `<i class='${icon}'></i><span class='step-verb'>${verb}: </span>${name}`
          }
          const option = {
            altLabel: name,
            label,
            id: item.sync_source_id
          }
          // `default` is a reserved word so cannot use object literal notation
          option.default = option.id === selected
          data.options.push(option)
        }
        return data
      },

      showNotePanel(ops) {
        if (ops == null) {
          ops = {}
        }

        ops = _.defaults(ops, { title: "" })
        if (ops.notes == null) {
          ops.notes = this.get("notes")
        }

        const panelOps = {
          panelWidthPerc: 0.7,
          style: {
            maxWidth: "650px"
          },
          title: ops.title || "",
          onWillClose: () => {
            App.getStore("layout").action.closeIn("panel")
            App.getStore("note").action.onCloseNotes()
          }
        }

        // const NotePanelContainer = withCohortSyncPlanContext((props) => (
        const NotePanelContainer = (props) => (
          <DockPanelContext.Consumer>
            {({ setHeader }) => (
              <ScrollView fill>
                <WrappedNotePanelView
                  {...props}
                  setHeader={setHeader}
                  onCreateNote={(...args) => {
                    props.onCreateNote && props.onCreateNote(...args)
                    // HACK: Apollo bug? This does refetch the sync plan queries but DOES NOT cause the SyncButton to update
                    // unclear as to why, but the query data in the SyncButton is stale
                    // Maybe related: https://github.com/apollographql/apollo-client/issues/3909
                    // appears that below works because the useSyncPlanContext is higher up in the tree, above the SyncButton
                    // props.refetchSyncPlanQueries &&
                    //   props.refetchSyncPlanQueries()
                    window.App &&
                      window.App.getStore("pathAdmin").storeTrigger(
                        "path:item:updated"
                      )
                  }}
                  onDeleteNote={(...args) => {
                    props.onDeleteNote && props.onDeleteNote(...args)
                    // HACK: Apollo bug? This does refetch the sync plan queries but DOES NOT cause the SyncButton to update
                    // unclear as to why, but the query data in the SyncButton is stale
                    // Maybe related: https://github.com/apollographql/apollo-client/issues/3909
                    // appears that below works because the useSyncPlanContext is higher up in the tree, above the SyncButton
                    // props.refetchSyncPlanQueries &&
                    //   props.refetchSyncPlanQueries()
                    window.App &&
                      window.App.getStore("pathAdmin").storeTrigger(
                        "path:item:updated"
                      )
                  }}
                />
              </ScrollView>
            )}
          </DockPanelContext.Consumer>
        )
        // ))

        window.App.getStore("layout").action.showIn(
          "panel",
          (props) => (
            <NotePanelContainer
              {...props}
              cohortId={get(App.getStore("resource").get("offering"), "id")}
            />
          ),
          ops,
          panelOps
        )
      },

      getContextStr(sync_source_id) {
        const { item } = this.getState()
        const ctx = this.getStore("path").request.getContext()
        let acontext = {
          course: ctx.resource,
          class: ctx.offering
        }
        if (sync_source_id) {
          acontext.step = sync_source_id
        } else if (item) {
          acontext.step = item.sync_source_id
        }

        acontext = (() => {
          const result = []
          for (let op in acontext) {
            result.push(`${op}_${acontext[op]}`)
          }
          return result
        })()

        return acontext.join(",")
      },

      getNotePermissions(note) {
        // for only viewing notes, removes control icons
        if (this.get("mode") === "view") {
          return {
            can_modify: false,
            can_post_curriculum_note: false,
            can_post_course_note: false,
            can_post_note: false
          }
        }

        const { user, session } = window.App.getStore("auth").getState()
        const { registration, offering, resource } =
          window.App.getStore("resource").getState()
        const permissions =
          window.App.getStore("resource").request.permissions()
        const role = registration ? registration.role : null
        const noteIsLocalToOffering =
          note &&
          note.get("context") &&
          note.get("context").offering ==
            note.get("context").created_from_offering

        const perms = {
          can_modify: false,
          can_post_curriculum_note: false,
          can_post_course_note: false,
          can_post_note: false
        }

        if (!session.is_authenticated) {
          return perms
        }

        const hasAdmnPrivs =
          (session.school_membership != null
            ? session.school_membership.is_school_admin
            : undefined) ||
          (user != null ? user.is_superuser : undefined) ||
          (permissions && permissions.can_edit)

        // check if offering status is "completed"
        if (
          offering.status === COHORT_STATUS_COMPLETED &&
          !hasAdmnPrivs &&
          role < 20
        ) {
          // no perms
          return perms
        }

        if (hasAdmnPrivs) {
          perms.can_modify = true
          perms.can_post_curriculum_note = true
          perms.can_post_course_note = true
          perms.can_post_note = true
        } else if (role >= 10) {
          perms.can_post_curriculum_note = true
          perms.can_post_course_note = true
          perms.can_post_note = true
          if (noteIsLocalToOffering || role >= 20) {
            perms.can_modify = true
          }
        } else if (role >= 5) {
          perms.can_post_note = true
        }
        // Author perms
        if (note && note.get("author")) {
          if (user && user.id === note.get("author").id) {
            perms.can_modify = true
          }
        }

        if (!noteIsLocalToOffering) {
          // Catch the licensee situation
          const { license_offerings } = resource
          const { resource_license } = offering
          // member created group
          const licensed_form_same_school =
            resource_license &&
            license_offerings.find(
              (rlo) => resource_license.license_offering == rlo.id
            )
          const licensed_from_another_school = !(
            !resource_license ||
            license_offerings.find(
              (rlo) => resource_license.license_offering == rlo.id
            )
          )
          if (licensed_form_same_school || licensed_from_another_school) {
            // You can't modify curriculum notes if you're a licensee
            perms.can_modify = false
          }
        }

        return perms
      }
    }
  }

  defaults() {
    return {
      notes: null, // the collection of notes for the path
      notes_per_target: null, // notes grouped based on target
      removed_note_ids: [],
      note_counter: null,
      item: null, // the group or step this note will be attached to
      mode: "edit",
      // UI
      is_loading_notes: false
    }
  }

  initializeStore() {}

  // Helpful utility function for processing path items
  // so they can be looped through
  _getAllPathItems() {
    const items = _.filter(this.getStore("path").getState().items)
    return _.flattenDeep(
      Array.from(items)
        // Get all root path items other than topics (aka dividers)
        .filter((i) => !i.parent && i.item_type !== "T")
        .map((i) => [i, i.items || []])
    )
  }
}
NoteStore.initClass()

export default window.App.stores.registerStore("note", NoteStore)

function __guard__(value, transform) {
  return typeof value !== "undefined" && value !== null
    ? transform(value)
    : undefined
}
